/**
 * Story Counter Generator
 *
 * Converts a story index (1-based) into the counter display text
 * based on the show template's counterPattern.
 *
 * Patterns:
 *   'खबर नंबर {n}'     → 'खबर नंबर 1'
 *   'NEWS #{n:02}'      → 'NEWS #01'
 *   'TOP STORY {n}'     → 'TOP STORY 1'
 *   'BIG NEWS #{n:02}'  → 'BIG NEWS #01'
 *   '{n}. खबर'         → '1. खबर'
 */

/**
 * Hindi digit map for optional Devanagari number rendering.
 */
const HINDI_DIGITS: Record<string, string> = {
  '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
  '5': '५', '6': '६', '7': '७', '8': '८', '9': '९',
};

function toHindiDigits(n: number): string {
  return String(n)
    .split('')
    .map((d) => HINDI_DIGITS[d] ?? d)
    .join('');
}

function zeroPad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/**
 * Generate the counter display text for a story.
 *
 * @param index     1-based story number
 * @param pattern   Template string — supports {n}, {n:02}, {hn} (Hindi digits)
 * @returns         Formatted counter text
 *
 * @example
 * generateCounterText(3, 'खबर नंबर {n}')     // → 'खबर नंबर 3'
 * generateCounterText(3, 'NEWS #{n:02}')      // → 'NEWS #03'
 * generateCounterText(3, 'खबर {hn}')          // → 'खबर ३'
 */
export function generateCounterText(index: number, pattern: string): string {
  return pattern
    .replace(/\{n:0(\d)\}/g, (_, w) => zeroPad(index, parseInt(w, 10)))
    .replace(/\{n\}/g, String(index))
    .replace(/\{hn\}/g, toHindiDigits(index));
}

// ─── Common counter presets ───────────────────────────────────

export const COUNTER_PRESETS: { label: string; pattern: string; preview: (n: number) => string }[] = [
  {
    label: 'खबर नंबर {n}',
    pattern: 'खबर नंबर {n}',
    preview: (n) => generateCounterText(n, 'खबर नंबर {n}'),
  },
  {
    label: 'NEWS #{n:02}',
    pattern: 'NEWS #{n:02}',
    preview: (n) => generateCounterText(n, 'NEWS #{n:02}'),
  },
  {
    label: 'TOP STORY {n:02}',
    pattern: 'TOP STORY {n:02}',
    preview: (n) => generateCounterText(n, 'TOP STORY {n:02}'),
  },
  {
    label: 'BIG NEWS #{n}',
    pattern: 'BIG NEWS #{n}',
    preview: (n) => generateCounterText(n, 'BIG NEWS #{n}'),
  },
  {
    label: 'खबर {n:02}',
    pattern: 'खबर {n:02}',
    preview: (n) => generateCounterText(n, 'खबर {n:02}'),
  },
  {
    label: 'फटाफट खबर {n}',
    pattern: 'फटाफट खबर {n}',
    preview: (n) => generateCounterText(n, 'फटाफट खबर {n}'),
  },
  {
    label: 'खबर नंबर {hn}',
    pattern: 'खबर नंबर {hn}',
    preview: (n) => generateCounterText(n, 'खबर नंबर {hn}'),
  },
];
